import { useState } from 'react';
import { useGameStore } from '../store';
import { HOUSING_TIERS } from '../data/housing';

/**
 * Shop component for purchasing housing upgrades
 * Displays all 7 tiers with status indicators and purchase flow
 */
export function Shop() {
  const [purchaseResult, setPurchaseResult] = useState(null);

  const mBalance = useGameStore((state) => state.mBalance);
  const housingLevel = useGameStore((state) => state.housingLevel);
  const purchaseHousing = useGameStore((state) => state.purchaseHousing);
  const canAffordHousing = useGameStore((state) => state.canAffordHousing);

  const handlePurchase = (tierLevel) => {
    const result = purchaseHousing(tierLevel);
    if (result.success) {
      setPurchaseResult({
        success: true,
        tierName: HOUSING_TIERS.find(t => t.id === tierLevel)?.name
      });
      // Auto-dismiss after 2 seconds
      setTimeout(() => setPurchaseResult(null), 2000);
    } else {
      setPurchaseResult({
        success: false,
        reason: result.reason
      });
      setTimeout(() => setPurchaseResult(null), 2000);
    }
  };

  const getTierStatus = (tier) => {
    if (tier.id < housingLevel) {
      return 'owned-previous';
    } else if (tier.id === housingLevel) {
      return 'owned-current';
    } else if (tier.id === housingLevel + 1) {
      return 'next';
    } else {
      return 'locked';
    }
  };

  const getTierStyles = (status, canAfford) => {
    switch (status) {
      case 'owned-current':
        return {
          container: 'border-gold bg-yellow-900/30',
          badge: 'bg-green-600 text-white',
          badgeText: 'CURRENT',
          nameColor: 'text-gold',
          costColor: 'text-gray-500'
        };
      case 'owned-previous':
        return {
          container: 'border-gray-600 bg-gray-800/50',
          badge: 'bg-gray-600 text-gray-300',
          badgeText: 'OWNED',
          nameColor: 'text-gray-400',
          costColor: 'text-gray-600'
        };
      case 'next':
        return {
          container: canAfford
            ? 'border-teal bg-teal/10'
            : 'border-gray-500 bg-gray-800/30',
          badge: null,
          badgeText: null,
          nameColor: canAfford ? 'text-teal' : 'text-gray-300',
          costColor: canAfford ? 'text-teal' : 'text-red-400'
        };
      case 'locked':
      default:
        return {
          container: 'border-gray-700 bg-gray-900/50 opacity-60',
          badge: 'bg-gray-700 text-gray-500',
          badgeText: 'LOCKED',
          nameColor: 'text-gray-500',
          costColor: 'text-gray-600'
        };
    }
  };

  return (
    <div className="w-full max-w-md mt-6">
      {/* Header */}
      <h2 className="font-pixel text-teal text-lg mb-4 text-center">
        Housing Shop
      </h2>

      {/* Current Balance Display */}
      <div className="text-center mb-4 p-2 bg-gray-800 rounded border border-gray-700">
        <span className="text-gray-400 text-sm">Your M Balance: </span>
        <span className="text-gold font-pixel text-lg">{mBalance.toLocaleString()}</span>
      </div>

      {/* Purchase Result Notification */}
      {purchaseResult && (
        <div className={`mb-4 p-3 rounded text-center ${
          purchaseResult.success
            ? 'bg-green-700 border border-green-500'
            : 'bg-red-700 border border-red-500'
        }`}>
          {purchaseResult.success ? (
            <p className="text-white text-sm">
              Upgraded to <span className="font-bold">{purchaseResult.tierName}</span>!
            </p>
          ) : (
            <p className="text-white text-sm">
              {purchaseResult.reason === 'insufficient_funds'
                ? 'Not enough M!'
                : 'Cannot purchase this tier'}
            </p>
          )}
        </div>
      )}

      {/* Tier List */}
      <div className="space-y-3">
        {HOUSING_TIERS.map((tier) => {
          const status = getTierStatus(tier);
          const canAfford = canAffordHousing(tier.id);
          const styles = getTierStyles(status, canAfford);
          const isNextTier = status === 'next';

          return (
            <div
              key={tier.id}
              className={`border-2 rounded-lg p-3 ${styles.container} transition-all duration-200`}
            >
              {/* Tier Header */}
              <div className="flex justify-between items-center mb-1">
                <div className="flex items-center gap-2">
                  <span className={`font-pixel text-sm ${styles.nameColor}`}>
                    {tier.name}
                  </span>
                  {styles.badge && (
                    <span className={`text-xs px-2 py-0.5 rounded ${styles.badge}`}>
                      {styles.badgeText}
                    </span>
                  )}
                </div>
                {tier.cost > 0 && (
                  <span className={`font-pixel text-sm ${styles.costColor}`}>
                    {tier.cost.toLocaleString()} M
                  </span>
                )}
              </div>

              {/* Description */}
              <p className="text-xs text-gray-500 mb-2">
                {tier.description}
              </p>

              {/* Purchase Button (only for next tier) */}
              {isNextTier && (
                <button
                  onClick={() => handlePurchase(tier.id)}
                  disabled={!canAfford}
                  className={`w-full py-2 rounded text-sm font-bold transition-all duration-200 ${
                    canAfford
                      ? 'bg-teal text-dark hover:opacity-80 cursor-pointer'
                      : 'bg-gray-700 text-gray-500 cursor-not-allowed'
                  }`}
                >
                  {canAfford ? 'UPGRADE' : `Need ${(tier.cost - mBalance).toLocaleString()} more M`}
                </button>
              )}
            </div>
          );
        })}
      </div>

      {/* Max Tier Message */}
      {housingLevel === 6 && (
        <div className="mt-4 p-3 border-2 border-purple-500 bg-purple-900/30 rounded-lg text-center">
          <p className="font-pixel text-purple-400 text-sm">
            Maximum Tier Achieved!
          </p>
          <p className="text-xs text-purple-300 mt-1">
            You've reached the Beachfront Estate - the ultimate retirement paradise!
          </p>
        </div>
      )}
    </div>
  );
}
